/**
 * Admin Dashboard Component
 *
 * Admin-only dashboard for viewing:
 * - All workflows across all users
 * - Detailed token usage by model
 * - Cost breakdown per workflow
 * - User activity and billing verification
 *
 * Access: First user created (admin only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  Clock,
  Users,
  Activity,
  TrendingUp,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Loader,
  AlertTriangle,
  Tag,
  Zap
} from 'lucide-react';
import { getWorkflowIdentifier } from '../utils/workflowIdentifier';
import axios from 'axios';

/**
 * Format cost with proper precision
 */
const formatCost = (cost) => {
  if (cost === null || cost === undefined) return 'N/A';
  if (cost < 0.0001) return `$${cost.toFixed(8)}`;
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  return `$${cost.toFixed(4)}`;
};

/**
 * Format large numbers with commas
 */
const formatNumber = (num) => {
  if (!num) return '0';
  return num.toLocaleString();
};

/**
 * Format date
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Individual workflow row with expandable details
 */
function WorkflowRow({ workflow, expanded, onToggle }) {
  const workflowIdentifier = getWorkflowIdentifier({ id: workflow.workflow_id });
  const costBreakdown = workflow.cost_breakdown || {};
  const modelBreakdown = costBreakdown.by_model || {};

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-6 py-4 whitespace-nowrap">
          <button className="text-gray-600 hover:text-gray-900">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </td>
        <td className="px-6 py-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Tag size={12} className="text-blue-600" />
              <span className="text-xs font-mono text-blue-700">{workflowIdentifier}</span>
            </div>
            <div className="text-sm text-gray-900 truncate max-w-md">
              {workflow.task_description || 'Untitled Workflow'}
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">{workflow.user_email || workflow.user_id}</div>
          <div className="text-xs text-gray-500">User ID: {workflow.user_id?.substring(0, 8)}...</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 text-xs rounded-full ${
            workflow.status === 'completed' ? 'bg-green-100 text-green-800' :
            workflow.status === 'failed' ? 'bg-red-100 text-red-800' :
            workflow.status === 'executing' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {workflow.status}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <div className="text-sm font-semibold text-gray-900">
            {formatCost(workflow.actual_cost || workflow.total_cost)}
          </div>
          <div className="text-xs text-gray-500">
            {workflow.total_nodes || 0} nodes
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(workflow.created_at)}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-blue-50">
          <td colSpan="6" className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Model Usage Breakdown */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Model Usage & Costs</h4>
                {Object.keys(modelBreakdown).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(modelBreakdown).map(([model, data]) => (
                      <div key={model} className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Zap size={14} className="text-purple-600" />
                            <span className="text-sm font-medium text-gray-900">{model}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCost(data.total_cost)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="font-medium">Input:</span> {formatNumber(data.total_input_tokens)} tokens
                          </div>
                          <div>
                            <span className="font-medium">Output:</span> {formatNumber(data.total_output_tokens)} tokens
                          </div>
                          <div>
                            <span className="font-medium">Nodes:</span> {data.node_count || 0}
                          </div>
                          <div>
                            <span className="font-medium">Avg/node:</span> {formatCost(data.average_cost_per_node)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No model breakdown available</p>
                )}
              </div>

              {/* Node Type Breakdown */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Node Type Breakdown</h4>
                {costBreakdown.by_node_type && Object.keys(costBreakdown.by_node_type).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(costBreakdown.by_node_type).map(([nodeType, data]) => (
                      <div key={nodeType} className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900 capitalize">
                              {nodeType.replace(/_/g, ' ')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {data.node_count} nodes • {formatNumber(data.total_tokens)} tokens
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCost(data.total_cost)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No node type breakdown available</p>
                )}
              </div>
            </div>

            {/* Workflow Metadata */}
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                <div>
                  <span className="font-medium">Workflow ID:</span>
                  <div className="font-mono text-blue-700">{workflow.workflow_id}</div>
                </div>
                <div>
                  <span className="font-medium">Duration:</span>
                  <div>{workflow.actual_duration_seconds ? `${workflow.actual_duration_seconds}s` : 'N/A'}</div>
                </div>
                <div>
                  <span className="font-medium">Completed:</span>
                  <div>{formatDate(workflow.completed_at)}</div>
                </div>
                <div>
                  <span className="font-medium">Total Tokens:</span>
                  <div>{formatNumber(workflow.total_input_tokens + workflow.total_output_tokens)}</div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Main Admin Dashboard Component
 */
export default function AdminDashboard() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedWorkflowId, setExpandedWorkflowId] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Summary stats
  const [stats, setStats] = useState({
    totalCost: 0,
    totalWorkflows: 0,
    totalUsers: 0,
    avgCostPerWorkflow: 0,
  });

  /**
   * Fetch admin workflow data
   * Note: This requires a backend admin endpoint
   */
  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual admin endpoint
      // This endpoint should return ALL workflows across ALL users
      const response = await axios.get('/api/v1/admin/workflows', {
        params: {
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          status: statusFilter || undefined,
          user_id: userFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          include_cost_breakdown: true,
          cost_breakdown_detail: 'full',
        },
      });

      setWorkflows(response.data.workflows || []);
      setTotalCount(response.data.total_count || 0);
      setStats(response.data.summary || stats);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, userFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  /**
   * Export data to CSV
   */
  const handleExport = () => {
    const csv = [
      ['Workflow ID', 'User ID', 'Status', 'Total Cost', 'Total Nodes', 'Created At', 'Task Description'].join(','),
      ...workflows.map(w => [
        w.workflow_id,
        w.user_id,
        w.status,
        w.actual_cost || w.total_cost || 0,
        w.total_nodes || 0,
        w.created_at,
        `"${(w.task_description || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-workflows-${new Date().toISOString()}.csv`;
    a.click();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && workflows.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Workflow costs and token usage across all users</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <DollarSign size={18} />
              <span className="text-sm font-medium">Total Cost</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCost(stats.totalCost)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Activity size={18} />
              <span className="text-sm font-medium">Total Workflows</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(stats.totalWorkflows)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Users size={18} />
              <span className="text-sm font-medium">Active Users</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(stats.totalUsers)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <TrendingUp size={18} />
              <span className="text-sm font-medium">Avg Cost/Workflow</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCost(stats.avgCostPerWorkflow)}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="executing">Executing</option>
                <option value="paused">Paused</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">User ID</label>
              <input
                type="text"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder="Filter by user ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={fetchAdminData}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                title="Export to CSV"
              >
                <Download size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-900">Error loading data</div>
              <div className="text-sm text-red-700 mt-1">{error}</div>
            </div>
          </div>
        )}

        {/* Workflows Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workflow</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {workflows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No workflows found
                  </td>
                </tr>
              ) : (
                workflows.map((workflow) => (
                  <WorkflowRow
                    key={workflow.workflow_id}
                    workflow={workflow}
                    expanded={expandedWorkflowId === workflow.workflow_id}
                    onToggle={() => setExpandedWorkflowId(
                      expandedWorkflowId === workflow.workflow_id ? null : workflow.workflow_id
                    )}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> workflows
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
